import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const BreadcrumbContainer = styled.nav`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 14px 0;
  border-bottom: 2px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 60px;
  z-index: 100;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    top: 55px;
    padding: 12px 0;
  }
`;

const BreadcrumbWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  width: 100%;
  display: flex;
  align-items: center;
  
  @media (max-width: 1024px) {
    padding: 0 30px;
  }
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const BreadcrumbList = styled.ol`
  display: flex;
  align-items: center;
  list-style: none;
  padding: 0;
  margin: 0;
  flex-wrap: wrap;
  gap: 8px;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  font-size: 18px;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const BreadcrumbLink = styled(Link)`
  color: #232B3B;
  text-decoration: none;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 215, 0, 0.15);
    color: #10a37f;
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: 3px solid #ffd700;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    padding: 5px 10px;
  }
`;

const CurrentPage = styled.span`
  color: #10a37f;
  font-weight: 700;
  padding: 6px 12px;
  background: rgba(16, 163, 127, 0.08);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 5px 10px;
  }
`;

const Separator = styled.span`
  color: #adb5bd;
  margin: 0 4px;
  font-size: 20px;
  font-weight: 300;
  user-select: none;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin: 0 2px;
  }
`;

const HomeIcon = styled.span`
  font-size: 22px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

interface BreadcrumbProps {
  customPaths?: { [key: string]: string };
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ customPaths = {} }) => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);
  
  // Route labels for better readability
  const routeLabels: { [key: string]: string } = {
    'about': 'About Us',
    'tools': 'Analysis Tools',
    'deep-dive': 'Deep Dive Tool',
    'quick-answer': 'Quick Answer',
    'testimonials': 'Client Stories',
    'contact': 'Contact',
    'privacy': 'Privacy Policy',
    'terms': 'Terms of Service',
    'transparency': 'Our Transparency',
    'inflation-protection': 'Inflation Protection',
    'philosophy': 'Our Philosophy',
    ...customPaths
  };
  
  // Don't show breadcrumbs on homepage
  if (pathnames.length === 0) {
    return null;
  }
  
  return (
    <BreadcrumbContainer aria-label="Breadcrumb navigation">
      <BreadcrumbWrapper>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to="/" aria-label="Go to homepage">
              <HomeIcon>🏠</HomeIcon>
              <span>Home</span>
            </BreadcrumbLink>
          </BreadcrumbItem>
          
          {pathnames.map((name, index) => {
            const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
            const isLast = index === pathnames.length - 1;
            const label = routeLabels[name] || name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
            
            return (
              <React.Fragment key={name}>
                <Separator aria-hidden="true">›</Separator>
                <BreadcrumbItem>
                  {isLast ? (
                    <CurrentPage aria-current="page">{label}</CurrentPage>
                  ) : (
                    <BreadcrumbLink to={routeTo}>{label}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </BreadcrumbWrapper>
    </BreadcrumbContainer>
  );
};